import { React, useEffect, useState, useRef, useCallback } from 'react'
import { Button, Modal, Form, OverlayTrigger, Tooltip, Image, Figure, Dropdown, Alert } from "react-bootstrap";
import { ApiManager } from "../../ApiManager"
import { handleApiError } from "../../utils/apiErrorHandler";
import { ThreeDots } from 'react-loader-spinner'

const ProjectStrategyEvaluationModal = (props) => {
    const project = props.project;
    const chapter = props.chapter;
    const strategy = props.strategy;
    const setReloadChapter = props.setReloadChapter;
    const setShowEvaluationModal = props.setShowEvaluationModal;
    const showEvaluationModal = props.showEvaluationModal;

    const [strat, setStrat] = useState({});
    const [strategyObservations, setStrategyObservations] = useState(strat !== undefined ? strat.Observations : "");
    const [strategyRecommendations, setStrategyRecommendations] = useState(strat !== undefined ? strat.Recommendations : "");
    const [strategyPriority, setStrategyPriority] = useState(strat !== undefined ? strat.Priority : "");
    const [strategyComplies, setStrategyComplies] = useState(null);
    const [strategyAgreedUpon, setStrategyAgreedUpon] = useState(false);
    const [badgeBg, setBadgeBg] = useState("success");
    const [badgeTooltip, setBadgeTooltip] = useState("default");
    const [prevDisabled, setPrevDisabled] = useState(false);
    const [nextDisabled, setNextDisabled] = useState(false);
    const [strategyPhotosToUpload, setStrategyPhotosToUpload] = useState([]);
    const [strategyPhotos, setStrategyPhotos] = useState([]);
    const [photoClicked, setPhotoClicked] = useState([]);
    const [photoRotated, setPhotoRotated] = useState(false);
    const fileInputRef = useRef(null);
    const isUploadingRef = useRef(false);
    const isSavingStrategyRef = useRef(false);
    const [saveError, setSaveError] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [photoTypeError, setPhotoTypeError] = useState(false);
    const [skippedDuplicates, setSkippedDuplicates] = useState([]);
    const [showDeletePrompt, setShowDeletePrompt] = useState(false);
    const [allRecommendations, setAllRecommendations] = useState([]);
    const [showRecommendationTextBox, setShowRecommendationTextBox] = useState(true);
    const [recommendationToggleHeader, setRecommendationToggleHeader] = useState("Saved Recommendations");
    const [isLoadingStrategyPhotoImages, setIsLoadingStrategyPhotoImages] = useState(true);

    const currentStrategyData = () => {
        var strategyToSave = {
            Description: strat.Description,
            Observations: strategyObservations !== undefined ? strategyObservations : strat.Observations,
            Recommendations: strategyRecommendations !== undefined ? strategyRecommendations : strat.Recommendations,
            Priority: strategyPriority !== undefined ? Number(strategyPriority) : strat.Priority,
            Enabled: true,
            Complies: strategyComplies !== undefined ? strategyComplies : strat.Complies,
            AgreedUpon: strategyAgreedUpon,
            ChapterId: chapter.Id,
            Id: strat.Id,
        };
        return strategyToSave;
    }

    const handlePrevious = async (shouldSetStrategy) => {
        if (!await saveStrategy()) return;
        var enabledStrategies = chapter.Strategies.filter((strat) => strat.Enabled === true);
        let index = enabledStrategies.map((strategy) => strategy.Id).indexOf(strat.Id);
        let indexPrev = index - 1;
        let indexBeforePrev = index - 2;

        if (indexPrev > -1) {
            if (shouldSetStrategy)
                setStrat(enabledStrategies[indexPrev]);
            setNextDisabled(false);
        }
        if (indexBeforePrev < 0)
            setPrevDisabled(true);
    };

    const handleNext = async (shouldSetStrategy) => {
        if (!await saveStrategy()) return;
        var enabledStrategies = chapter.Strategies.filter((strat) => strat.Enabled === true);
        let index = enabledStrategies.map((strategy) => strategy.Id).indexOf(strat.Id);
        let indexNext = index + 1;
        let indexAfterNext = index + 2;

        if (indexNext < enabledStrategies.length) {
            if (shouldSetStrategy)
                setStrat(enabledStrategies[indexNext]);
            setPrevDisabled(false);
        }
        if (indexAfterNext > enabledStrategies.length - 1)
            setNextDisabled(true);
    };

    const setInitalPreviousNextStates = useCallback(() => {
        var enabledStrategies = chapter.Strategies.filter((strat) => strat.Enabled === true);
        let index = enabledStrategies.map((strategy) => strategy.Id).indexOf(strat.Id);
        let indexPrev = index - 1;
        let indexNext = index + 1;

        indexPrev < 0 ? setPrevDisabled(true) : setPrevDisabled(false);
        indexNext > enabledStrategies.length - 1 ? setNextDisabled(true) : setNextDisabled(false);
    }, [chapter.Strategies, strat.Id]);

    const getStrategyPhotosAndSetState = useCallback((strat) => {
        ApiManager.getInstance().getProjectStrategyPhotosByStrategyId(strat.Id).then((response) => {
            setStrategyPhotos(response);
        }).catch(handleApiError).finally(() => {
            setIsLoadingStrategyPhotoImages(false);
        });
    }, []);

    const getAllRecommendations = () => {
        ApiManager.getInstance().getAllRecommendations().then((result) => {
            setAllRecommendations(result.data);
        }).catch(handleApiError);
    }

    useEffect(() => {
        if (Array.isArray(strategyPhotosToUpload) && strategyPhotosToUpload.length > 0 && !isUploadingRef.current) {
            isUploadingRef.current = true;
            setIsUploading(true);
            const formData = new FormData();
            formData.append('StrategyId', strat.Id);

            const currentStrategy = currentStrategyData();
            for (let i = 0; i < strategyPhotosToUpload.length; i++)
                formData.append('Photos', strategyPhotosToUpload[i]);

            ApiManager.getInstance().createProjectStrategyPhotos(formData).then(() => {
                fileInputRef.current.value = "";
                setStrategyPhotosToUpload([]);
                isUploadingRef.current = false;
                setIsUploading(false);
                getStrategyPhotosAndSetState(currentStrategy);
            }).catch((error) => {
                fileInputRef.current.value = "";
                setStrategyPhotosToUpload([]);
                isUploadingRef.current = false;
                setIsUploading(false);
                handleApiError(error);
            });
        }
    }, [strategyPhotosToUpload]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (showEvaluationModal && Object.keys(strat).length === 0) {
            let s = chapter.Strategies.find(s => s.Id === strategy.Id);
            if (s != null)
                setStrat(s);
        } else if (!showEvaluationModal && Object.keys(strat).length > 0) {
            setStrat({});
        }
    }, [showEvaluationModal]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (strat !== undefined && Object.keys(strat).length !== 0) {
            setIsLoadingStrategyPhotoImages(true);
            setStrategyPhotos([]);
            getAllRecommendations();
            setStrategyPriority(strat.Priority);
            setBadgeBgSwitch(strat.Priority);
            setStrategyObservations(strat.Observations);
            setStrategyRecommendations(strat.Recommendations);
            setStrategyComplies(strat.Complies);
            setStrategyAgreedUpon(!!strat.AgreedUpon);
            setShowDeletePrompt(false);
            setSkippedDuplicates([]);
            setSaveError(false);
            setInitalPreviousNextStates();            
            if (strat.Id !== undefined)
                getStrategyPhotosAndSetState(strat);
            else
                setIsLoadingStrategyPhotoImages(false);
                       
        }
    }, [getStrategyPhotosAndSetState, strat, setInitalPreviousNextStates]);

    useEffect(() => {
        if (allRecommendations.length > 0) {
            var enabledRecommendations = allRecommendations.filter((rec) => rec.Enabled === true);
            let index = enabledRecommendations.map((rec) => rec.Value).indexOf(strat.Recommendations);

            if (index > -1) {
                setRecommendationToggleHeader(enabledRecommendations[index].Value);
                setShowRecommendationTextBox(false);
            }
            else {
                setRecommendationToggleHeader("Custom");
                setShowRecommendationTextBox(true);
            }
        }
    }, [allRecommendations]) // eslint-disable-line react-hooks/exhaustive-deps

    const deleteImage = () => {
        ApiManager.getInstance().deleteProjectStrategyPhoto(photoClicked.Id).then(() => {
            setShowDeletePrompt(false);
            var currentStrategy = currentStrategyData();
            getStrategyPhotosAndSetState(currentStrategy);
        }).catch(handleApiError);
    };

    const onObservationsChanged = (val) => {
        setStrategyObservations(val);
    };

    const onCompliesChanged = (val) => {
        const num = parseInt(val);
        setStrategyComplies(isNaN(num) || num === 0 ? null : num);
    };

    const onPriorityChanged = (val) => {
        setStrategyPriority(val);
        setBadgeBgSwitch(Number(val));
    };

    // when user types their own
    const onRecommendationsChanged = (val) => {
        setStrategyRecommendations(val);
    };

    // when user chooses one from the dropdown
    const cannedRecommendationClicked = (rec) => {
        setStrategyRecommendations(rec.Value);
        setRecommendationToggleHeader(rec.Value);
        setShowRecommendationTextBox(false);
    }

    // When user chooses the 'custom' choice from the dropdown
    const customRecommendationClicked = () => {
        setShowRecommendationTextBox(true);
        setRecommendationToggleHeader("Custom");
    }

    const setBadgeBgSwitch = (priority) => {
        switch (priority) {
            default:
                break;
            case 1:
                setBadgeBg("#FFE55E");
                setBadgeTooltip("A UD strategy having a relatively low cost (or no cost because of alignment with code requirements) with high impact to the organization and users");
                break;
            case 2:
                setBadgeBg("#58a7bf");
                setBadgeTooltip("A UD strategy having a higher cost with relative impact to the organization and users.");
                break;
            case 3:
                setBadgeBg("#A1D884");
                setBadgeTooltip("Driven by a specific owner requirement or unique opportunity");
                break;
        }
    }

    const onHideHandler = async () => {
        // Save FIRST — only close once the save succeeded, so a failure never
        // destroys what the user typed
        if (await saveStrategy())
            setShowEvaluationModal(false);
    };

    // Priority is a number from the API but a string once the <select> is touched
    const isDirty = () => {
        return strat.Observations !== strategyObservations ||
            strat.Recommendations !== strategyRecommendations ||
            Number(strat.Priority ?? 0) !== Number(strategyPriority ?? 0) ||
            strat.Complies !== strategyComplies ||
            !!strat.AgreedUpon !== strategyAgreedUpon;
    };

    // Returns true when it is safe to close/navigate. An unset Priority no longer
    // blocks saving — that guard used to silently discard every other edit.
    const saveStrategy = async () => {
        if (!isDirty()) return true;
        if (isSavingStrategyRef.current) return false;
        isSavingStrategyRef.current = true;
        try {
            await ApiManager.getInstance().updateProjectStrategy(currentStrategyData());
            setSaveError(false);
            setReloadChapter(true);
            return true;
        }
        catch (error) {
            setSaveError(true);
            return false;
        }
        finally {
            isSavingStrategyRef.current = false;
        }
    };

    const handleFileChange = (e) => {
        setPhotoTypeError(false);
        setSkippedDuplicates([]);
        const files = Array.from(e.target.files);
        const hasInvalidFile = files.some(f => {
            const ext = f.name.split('.').pop().toLowerCase();
            return ext === 'heic' || ext === 'heif' || f.type === 'image/heic' || f.type === 'image/heif';
        });
        if (hasInvalidFile) {
            fileInputRef.current.value = "";
            setPhotoTypeError(true);
            return;
        }
        const existingFileNames = strategyPhotos
            .filter(p => p.OriginalFileName)
            .map(p => p.OriginalFileName.toLowerCase());
        const duplicates = files.filter(f => existingFileNames.includes(f.name.toLowerCase()));
        const toUpload = files.filter(f => !existingFileNames.includes(f.name.toLowerCase()));
        if (duplicates.length > 0)
            setSkippedDuplicates(duplicates.map(f => f.name));
        if (toUpload.length === 0) {
            fileInputRef.current.value = "";
            return;
        }
        setStrategyPhotosToUpload(toUpload);
    };

    // Path is a SAS read URL now, so the raw tail of it is the blob GUID plus the whole
    // ?sv=...&sig=... query string. Prefer the name the user actually uploaded; fall back to
    // the blob name (query stripped) for photos stored before OriginalFileName existed.
    const photoDisplayName = (photo) => {
        if (!photo) return "Photo";
        if (photo.OriginalFileName) return photo.OriginalFileName;
        if (!photo.Path) return "Photo";
        try {
            return decodeURIComponent(new URL(photo.Path).pathname.split('/').pop()) || "Photo";
        }
        catch {
            return photo.Path.split('?')[0].split('/').pop() || "Photo";
        }
    };

    const ImageClicked = (photo) => {
        setShowDeletePrompt(true);
        setPhotoClicked(photo);
        setPhotoRotated(photo.Rotated);
    };

    const rotatedCheckboxOnChange = (checked) => {
        setPhotoRotated(checked);
        var strategyPhotoToSave = {
            Rotated: checked,
            Id: photoClicked.Id
        }
        ApiManager.getInstance().updateProjectStrategyPhoto(strategyPhotoToSave).catch(handleApiError);
    };

    return (
        <>
            <Modal show={showEvaluationModal} onHide={() => onHideHandler()} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {strat !== undefined ? strat.Description : ""}&nbsp;
                        {strategyPriority > 0 ?
                            <OverlayTrigger placement="bottom" overlay={<Tooltip id={"tooltip-strategy-badge"}>{badgeTooltip}</Tooltip>}>
                                <span className="badge" style={{ backgroundColor: badgeBg, color: 'black' }}>Priority {strategyPriority !== null ? strategyPriority : ""}</span>
                            </OverlayTrigger> :
                            <></>
                        }
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {saveError &&
                        <Alert variant="danger" dismissible onClose={() => setSaveError(false)}>
                            Saving failed — your changes are still here. Please try again, or copy your text before closing.
                        </Alert>}
                    {isLoadingStrategyPhotoImages ?
                        <div style={{ justifyContent: "center", display: "flex", margin: "0 auto" }}>
                            <ThreeDots color="var(--pc-red)" />
                        </div> :                    
                        <Form className="row g-3">
                            {project.ObservationsEnabled ? <Form.Group>
                                <Form.Label>Observations</Form.Label>
                                <Form.Control
                                    onChange={(e) => onObservationsChanged(e.target.value)} as="textarea" autoFocus rows={2}
                                    value={strategyObservations !== null ? strategyObservations : ""} />
                            </Form.Group> : ""}

                            {project.CompliesEnabled ? <Form.Group>
                                <Form.Label>Complies</Form.Label>
                                <Form.Select
                                    label="Complies"
                                    onChange={(e) => onCompliesChanged(e.target.value)}
                                    value={strategyComplies !== null ? strategyComplies.toString() : "0"}>
                                    <option value="0">Not Applicable</option>
                                    <option value="1">Yes</option>
                                    <option value="2">No</option>
                                    <option value="3">Sometimes</option>
                                </Form.Select>
                            </Form.Group> : ""}

                            <Form.Group>
                                <Form.Label>Priority</Form.Label>
                                <Form.Select
                                    label="priority"
                                    onChange={(e) => onPriorityChanged(e.target.value)}
                                    value={strategyPriority !== null ? strategyPriority : ""}
                                    style={{ cursor: 'auto' }}>
                                    <option value="0">Please select a Priority</option>
                                    <option value="1">1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group>
                                <Form.Check
                                    type="checkbox"
                                    id="strategy-agreed-upon-check"
                                    label={<span>Agreed Upon Strategy <span style={{ color: 'var(--pc-yellow)' }}>★</span></span>}
                                    checked={strategyAgreedUpon}
                                    onChange={(e) => setStrategyAgreedUpon(e.target.checked)}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Recommendations</Form.Label>
                                <Dropdown>
                                    <Dropdown.Toggle variant="outline-secondary" id="dropdown-Recommendations" className="mb-3">{recommendationToggleHeader}</Dropdown.Toggle>
                                    <Dropdown.Menu variant="dark">
                                        <Dropdown.Item
                                            onClick={() => customRecommendationClicked()}
                                            eventKey='customRecommendationEventKey'>Custom
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        {allRecommendations.map((rec) => rec.Enabled &&
                                            <Dropdown.Item
                                                key={rec.Value}
                                                onClick={() => cannedRecommendationClicked(rec)}
                                                eventKey={rec.Id}>{rec.Value}
                                            </Dropdown.Item>)}
                                    </Dropdown.Menu>
                                </Dropdown>
                                <Form.Control
                                    style={{ cursor: !showRecommendationTextBox ? 'not-allowed' : 'auto' }}
                                    disabled={!showRecommendationTextBox}
                                    onChange={(e) => onRecommendationsChanged(e.target.value)}
                                    as="textarea"
                                    autoFocus
                                    rows={2}
                                    value={strategyRecommendations !== null ? strategyRecommendations : ""} />
                                <Figure.Caption style={{ display: !showRecommendationTextBox ? 'inline' : 'none', color: '#006F62' }}>Select "Custom" in the Recommendations dropdown to type your own</Figure.Caption>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Upload Photo</Form.Label>
                                <Form.Control
                                    type="file"
                                    onChange={handleFileChange}
                                    ref={fileInputRef}
                                    accept=".jpg,.jpeg,.png"
                                    multiple
                                    disabled={isUploading} />
                                {isUploading && <Form.Text className="text-muted">Uploading...</Form.Text>}
                                {photoTypeError && <Form.Text className="text-danger">HEIC images are not supported. Please upload JPG or PNG files.</Form.Text>}
                                {skippedDuplicates.length > 0 && <Form.Text className="text-warning d-block">Already uploaded, skipped: {skippedDuplicates.join(', ')}</Form.Text>}
                                <div className="d-flex flex-wrap">
                                    {strategyPhotos.map((photo) =>
                                        <Image
                                            onClick={() => ImageClicked(photo)}
                                            key={photo.Path}
                                            src={photo.ByteData !== null ? `data:image/png;base64,${photo.ByteData}` : photo.Path}
                                            style={{
                                                width: "120px",
                                                height: "auto",
                                                cursor: "pointer",
                                                borderRadius: "4px",
                                                // Keep the border reserved at all times so selecting a photo
                                                // doesn't shift the whole strip by 3px
                                                border: "3px solid",
                                                borderColor: showDeletePrompt && photoClicked.Id === photo.Id
                                                    ? "var(--pc-red)"
                                                    : "transparent"
                                            }}
                                            className="m-2"></Image>
                                    )}
                                </div>
                                {showDeletePrompt &&
                                    <div className="border rounded p-3 mt-2">
                                        <div className="fw-bold">Selected photo</div>
                                        <div className="text-muted small text-break mb-3">{photoDisplayName(photoClicked)}</div>
                                        <Form.Check
                                            type="checkbox"
                                            id="photo-remove-rotation-check"
                                            label="Remove Rotation"
                                            checked={photoRotated}
                                            onChange={(e) => rotatedCheckboxOnChange(e.target.checked)} />
                                        <hr className="my-3" />
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                            <span className="text-danger fw-bold me-auto">Delete this photo?</span>
                                            <Button variant="outline-danger" onClick={() => deleteImage()}>Yes</Button>
                                            <Button variant="outline-secondary" onClick={() => setShowDeletePrompt(false)}>No</Button>
                                        </div>
                                    </div>
                                }
                            </Form.Group>
                        </Form>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => handlePrevious(true)} disabled={prevDisabled}>Previous</Button>
                    <Button variant="outline-secondary" onClick={() => handleNext(true)} disabled={nextDisabled}>Next</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ProjectStrategyEvaluationModal;